// Lịch: logic thuần (không đụng DOM) cho màn Lịch và hộp thoại sự kiện / nhắc hẹn — test unit import trực tiếp
// (Node chạy được file .ts nhờ type stripping → chỉ dùng cú pháp TS xóa được, chỉ `import type`).
import type { CalendarItem, CalendarScope } from '@swchat/protocol';

const pad = (n: number) => String(n).padStart(2, '0');

/** Khóa ngày theo giờ máy: `YYYY-MM-DD`. */
export const dayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Lưới tháng 6 tuần × 7 ngày, bắt đầu từ thứ Hai (giờ máy). */
export function monthGrid(cursor: Date): Date[] {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(first.getFullYear(), first.getMonth(), 1 - offset + i));
}

/** Khoảng `[from, to)` (ISO) bao trọn lưới tháng — dùng để hỏi server. */
export function gridRange(cursor: Date): { from: string; to: string } {
  const days = monthGrid(cursor);
  const last = days[41]!;
  return { from: days[0]!.toISOString(), to: new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1).toISOString() };
}

/** Các ngày (khóa ngày giờ máy) mà một lần diễn ra trải qua; sự kiện nhiều ngày hiện ở từng ngày (tối đa 31). */
export function itemDays(item: Pick<CalendarItem, 'occurrence_start' | 'occurrence_end'>): string[] {
  const start = new Date(item.occurrence_start);
  const days = [dayKey(start)];
  if (!item.occurrence_end) return days;
  // Kết thúc đúng 0h thì ngày đó không tính (kết thúc là mốc loại trừ).
  const end = new Date(new Date(item.occurrence_end).getTime() - 1);
  for (let d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1); d <= end && days.length < 31; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    days.push(dayKey(d));
  }
  return days;
}

export function groupByDay(items: readonly CalendarItem[]): Map<string, CalendarItem[]> {
  const map = new Map<string, CalendarItem[]>();
  for (const item of items) for (const key of itemDays(item)) map.set(key, [...(map.get(key) ?? []), item]);
  return map;
}

/** Giờ hiển thị của một lần diễn ra: "Cả ngày" hoặc `HH:mm` (kèm giờ kết thúc nếu có). */
export function timeLabel(item: Pick<CalendarItem, 'all_day' | 'occurrence_start' | 'occurrence_end'>): string {
  if (item.all_day) return 'Cả ngày';
  const t = (iso: string) => {
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  return item.occurrence_end ? `${t(item.occurrence_start)}–${t(item.occurrence_end)}` : t(item.occurrence_start);
}

export const REMIND_LABELS: Record<number, string> = {
  0: 'Đúng giờ',
  5: 'Trước 5 phút',
  10: 'Trước 10 phút',
  15: 'Trước 15 phút',
  30: 'Trước 30 phút',
  60: 'Trước 1 giờ',
  120: 'Trước 2 giờ',
  1440: 'Trước 1 ngày',
};

/** Nhãn cho thông báo nhắc: "Đã tới giờ" / "Còn 10 phút" / "Còn 1 giờ" / "Còn 1 ngày". */
export function offsetLabel(minutes: number): string {
  if (minutes <= 0) return 'Đã tới giờ';
  if (minutes < 60) return `Còn ${minutes} phút`;
  if (minutes < 1440) return `Còn ${Math.round(minutes / 60)} giờ`;
  return `Còn ${Math.round(minutes / 1440)} ngày`;
}

export const SCOPE_TONE: Record<CalendarScope, 'blue' | 'green' | 'amber'> = { personal: 'blue', project: 'green', company: 'amber' };

/** Giá trị cho `<input type="datetime-local">` (hoặc `date` khi cả ngày) theo giờ máy. */
export function toLocalInput(iso: string, allDay: boolean): string {
  const d = new Date(iso);
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return allDay ? date : `${date}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Ngược lại: giá trị ô nhập (giờ máy) → ISO UTC. Cả ngày = 0h giờ máy. */
export function fromLocalInput(value: string, allDay: boolean): string {
  return new Date(allDay ? `${value}T00:00` : value).toISOString();
}
